import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useState } from "react";
import ServiceFormDialog from "./ServiceFormDialog";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import AllServiceCategoriesLayer from "./AllServiceCategoriesLayer";
import Pagination from "../Pagination";

const NO_SUBCATEGORY = "none";

const AllServicesLayer = () => {
  const [records, setRecords] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [categoryRefresh, setCategoryRefresh] = useState(0);
  const [loading, setLoading] = useState(false);
  const [update, setUpdate] = useState(0);
  const [page, setPage] = useState(0);
  const [limit] = useState(10);
  const [categoryId, setCategoryId] = useState("");
  const [subcategoryId, setSubcategoryId] = useState(""); // "" | "none" | id
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [total, setTotal] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  const token = localStorage.getItem("token");
  const [serviceDialog, setServiceDialog] = useState(null); // null = closed, { service } = open (service null = add)
  const totalPages = Math.ceil(total / limit);

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}api/admin/service-categories?limit=100`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const json = await response.json();
        if (response.ok) {
          setCategories(json.data.categories || []);
        }
      } catch (error) {
        console.error("Error fetching service categories:", error);
      }
    };

    fetchCategories();
  }, [token, categoryRefresh]);

  // Subcategory filter options depend on the selected category
  useEffect(() => {
    if (!categoryId) {
      setSubcategories([]);
      return;
    }
    const fetchSubcategories = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}api/admin/service-subcategories?category_id=${categoryId}&limit=100`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const json = await response.json();
        if (response.ok) {
          setSubcategories(json.data.subcategories || []);
        }
      } catch (error) {
        console.error("Error fetching subcategories:", error);
      }
    };
    fetchSubcategories();
  }, [categoryId, token, categoryRefresh]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        const searchParams = new URLSearchParams({ page: String(page), limit: String(limit) });
        if (categoryId) searchParams.set("category_id", categoryId);
        if (subcategoryId === NO_SUBCATEGORY) {
          searchParams.set("no_subcategory", "true");
        } else if (subcategoryId) {
          searchParams.set("subcategory_id", subcategoryId);
        }
        if (debouncedSearch.trim()) searchParams.set("search", debouncedSearch.trim());
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}api/admin/services?${searchParams}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const json = await response.json();
        if (response.ok) {
          setRecords(json.data.services || []);
          const pagination = json.data.pagination || {};
          setTotal(pagination.total_count ?? pagination.total ?? 0);
          setHasNext(!!pagination.has_next);
          setHasPrev(!!pagination.has_prev);
        } else {
          setRecords([]);
        }
      } catch (error) {
        console.error("Error fetching services:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [update, page, limit, categoryId, subcategoryId, debouncedSearch, token]);

  const handleDelete = (serviceId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
      customClass: {
        confirmButton: "btn btn-success btn-sm ms-2",
        cancelButton: "btn btn-sm",
        popup: "my-custom-modal",
        title: "my-custom-title",
        icon: "my-custom-icon",
        content: "my-custom-text",
      },
      buttonsStyling: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}api/admin/services/${serviceId}`,
            {
              method: "DELETE",
              headers: { Authorization: `Bearer ${token}` },
            },
          );
          if (response.ok) {
            setUpdate((u) => u + 1);
            Swal.fire({
              title: "Deleted!",
              text: "Service has been deleted.",
              icon: "success",
            });
          } else {
            const json = await response.json().catch(() => ({}));
            toast.error(json.errorMessage || "Failed to delete service");
          }
        } catch (error) {
          console.error(error);
          Swal.fire({
            title: "Error",
            text: "Failed to delete service",
            icon: "error",
          });
        }
      }
    });
  };

  return (
    <>
      <AllServiceCategoriesLayer
        onCategoriesChanged={() => {
          setCategoryRefresh((current) => current + 1);
          setUpdate((current) => current + 1);
        }}
      />

      {/* <div className="card h-100 p-0 radius-12">
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <h6 className="text-md mb-0">Services</h6>
          <button
            type="button"
            className="btn btn-sm btn-primary-600"
            onClick={() => setServiceDialog({ service: null })}
          >
            <i className="ri-add-line" /> Add Service
          </button>
        </div>

        <div className="card-body p-24">
          <div className="row g-3 mb-20">
            <div className="col-md-4">
              <label htmlFor="service-search" className="form-label">Search services</label>
              <input
                id="service-search"
                type="search"
                className="form-control"
                placeholder="Search by service name"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
              />
            </div>
            <div className="col-md-4">
              <label htmlFor="service-category-filter" className="form-label">Category</label>
              <div className="position-relative">
                <select
                  id="service-category-filter"
                  className="form-select pe-5"
                  style={{ appearance: "none", WebkitAppearance: "none", backgroundImage: "none" }}
                  value={categoryId}
                  onChange={(e) => {
                    setCategoryId(e.target.value);
                    setSubcategoryId(""); // subcategory options change with the category
                    setPage(0);
                  }}
                >
                  <option value="">All categories</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </select>
                <Icon
                  icon="mdi:chevron-down"
                  className="position-absolute top-50 end-0 translate-middle-y me-3 text-secondary-light"
                  aria-hidden="true"
                />
              </div>
            </div>
            <div className="col-md-4">
              <label htmlFor="service-subcategory-filter" className="form-label">Subcategory</label>
              <div className="position-relative">
                <select
                  id="service-subcategory-filter"
                  className="form-select pe-5"
                  style={{ appearance: "none", WebkitAppearance: "none", backgroundImage: "none" }}
                  value={subcategoryId}
                  disabled={!categoryId}
                  onChange={(e) => {
                    setSubcategoryId(e.target.value);
                    setPage(0);
                  }}
                >
                  <option value="">
                    {categoryId ? "All subcategories" : "Select a category first"}
                  </option>
                  {categoryId && (
                    <option value={NO_SUBCATEGORY}>No subcategory (directly under category)</option>
                  )}
                  {subcategories.map((subcategory) => (
                    <option key={subcategory.id} value={subcategory.id}>{subcategory.name}</option>
                  ))}
                </select>
                <Icon
                  icon="mdi:chevron-down"
                  className="position-absolute top-50 end-0 translate-middle-y me-3 text-secondary-light"
                  aria-hidden="true"
                />
              </div>
            </div>
          </div>
          <div className="table-responsive scroll-sm">
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th scope="col">ID</th>
                  <th scope="col">Name</th>
                  <th scope="col">Category</th>
                  <th scope="col">Subcategory</th>
                  <th scope="col">Duration (min)</th>
                  <th scope="col" className="text-center">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      Loading...
                    </td>
                  </tr>
                ) : records.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      No services found
                    </td>
                  </tr>
                ) : (
                  records.map((service) => (
                    <tr key={service.id}>
                      <td>{service.id}</td>
                      <td>{service.name}</td>
                      <td>{service.category?.name || "-"}</td>
                      <td>{service.subcategory?.name || "-"}</td>
                      <td>{service.durationMinutes ?? "-"}</td>
                      <td className="text-center">
                        <div className="d-flex align-items-center gap-10 justify-content-center">
                          <button
                            onClick={() => setServiceDialog({ service })}
                            className="bg-success-focus bg-hover-success-200 text-success-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            title="Edit"
                          >
                            <Icon icon="lucide:edit" className="menu-icon" />
                          </button>
                          <button
                            onClick={() => handleDelete(service.id)}
                            className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            title="Delete"
                          >
                            <Icon
                              icon="fluent:delete-24-regular"
                              className="menu-icon"
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
            <span>
              Showing {records.length} of {total} entries
            </span>

            <Pagination
              currentPage={page}
              totalPages={totalPages}
              hasNext={hasNext}
              hasPrev={hasPrev}
              onPageChange={setPage}
            />
          </div>
        </div>
      </div> */}

      {serviceDialog && (
        <ServiceFormDialog
          token={token}
          service={serviceDialog.service}
          defaultCategoryId={categoryId}
          onClose={() => setServiceDialog(null)}
          onSaved={() => {
            setServiceDialog(null);
            setUpdate((u) => u + 1);
            setCategoryRefresh((c) => c + 1);
          }}
        />
      )}
    </>
  );
};

export default AllServicesLayer;